import React, { useContext, useEffect } from 'react'
import { Switch, Route, useHistory, Redirect, RouteComponentProps } from 'react-router-dom'
import { NavItem } from '../Navigation'
import { NavigationContext } from '../NavigationContext'
import DefaultPageNotFoundComponent from './DefaultPageNotFoundComponent'
import SupportCenter from 'widgets'

/**
 * Renders the necessary react-router switch and routes for a given NavConfig.
 * This should be used by the the Navigation UI components where the current tab should be displayed.
 */
const NavRoutes = (): JSX.Element => {
    const history = useHistory()
    const {
        basePath,
        getInitialNavItem,
        isOnBasePath,
        navItems: topLevelNavItems,
        getPathToNavItem,
        getNavItemComponentType,
        getInvalidPathNavItem
    } = useContext(NavigationContext)

    const renderNavItemRoute = (navItem: NavItem): JSX.Element | null => {
        const ComponentType = getNavItemComponentType(navItem)

        if (ComponentType) { // Ultimately if there is a component we need a route to reach it
            const currentItemPath = getPathToNavItem(navItem.id)
            if (currentItemPath) {
                const componentProps = navItem.componentProps || {}
                return (
                    <Route
                        key={navItem.id}
                        path={currentItemPath}

                        /* TODO: dont pass routeProps/routerProps, use either nav context helpers or something like useRouteMatch() in the child components */
                        render={props => <ComponentType routerProps={{ ...props }} {...componentProps} />}
                    />
                )
            }
        } else {
            return null
        }
    }

    const renderRoutesRecursive = (routes: JSX.Element[], navItem) => {
        const route = renderNavItemRoute(navItem)
        if (route) {
            routes.push(route)
        }
        (navItem.children || []).forEach(subNavItem => renderRoutesRecursive(routes, subNavItem))
    }

    useEffect(() => {
        if (isOnBasePath() || window.location.pathname === '/') {
            const initialNavItem = getInitialNavItem()
            const initialPath = initialNavItem ? getPathToNavItem(initialNavItem.id) : null
            if (initialPath) {
                history.push(initialPath)
            }
        }
    }, [])

    // Create one flat array of all react router routes (for the entire nav hierarchy)
    const routes: JSX.Element[] = []
    topLevelNavItems.forEach(navItem => renderRoutesRecursive(routes, navItem))

    return (
        <Switch>
            {routes}

            {topLevelNavItems.filter(n => n.route?.path).map(navItem => (
                <Route
                    key={`direct-route-for-${navItem.id}`}
                    path={`/${navItem.route.path}`}
                    render={(props: RouteComponentProps) => {
                        console.debug('NavRoutes: user has navigated to a top-level nav item (without baseURL), adding base URL to path and redirecting...')
                        const to = `${basePath}${props.location.pathname}${props.location.search}${props.location.hash}`
                        return <Redirect to={to} />
                    }}
                />
            ))}

            <Route
                key="support-center"
                path="/support-center"
                render={props => <SupportCenter routeProps={{ ...props }} />}
            />
            <Route
                key="support-center-with-extension"
                path={`${basePath}/support-center`}
                render={props => <SupportCenter routeProps={{ ...props }} />}
            />

            <Route
                path="*"
                render={() => {
                    console.error(`NavRoutes: user has navigated to an invalid path: ${window.location.pathname}`)
                    const invalidPathNavItem = getInvalidPathNavItem()
                    if (invalidPathNavItem) {
                        const path = getPathToNavItem(invalidPathNavItem.id)
                        console.error(`NavRoutes: redirecting to the configured invalid path nav item: ${path}`)
                        return <Redirect to={path} />
                    } else {
                        console.error('NavRoutes: no invalid path nav item configured so showing DefaultPageNotFoundComponent instead.')
                        return <DefaultPageNotFoundComponent />
                    }
                }}
            />
        </Switch>
    )
}

export default NavRoutes
